import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { SkillBadge } from "@/components/SkillBadge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Briefcase, Check, Download, User, X, Brain, MessageSquare, FileText, ClipboardCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReactMarkdown from 'react-markdown';
import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MatchComments } from "@/components/matches/MatchComments";
import { InterviewPrep } from "@/components/matches/InterviewPrep";

const MatchDetail = () => {

	const KANBAN_STATUSES = [
		{ id: 'matched', name: 'Matched' },
		{ id: 'screening', name: 'Screening' },
		{ id: 'sent', name: 'Sent' },
		{ id: 'interview', name: 'Interview' },
		{ id: 'offer', name: 'Offer' },
		{ id: 'refused', name: 'Refused' }
	];

	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [match, setMatch] = useState<any>(null);
	const [activeTab, setActiveTab] = useState("overview");
	const [openAccordionItems, setOpenAccordionItems] = useState<string[]>(["analysis", "strengths", "weaknesses", "skills"]);
	
	const isAIAnalysisInProgress = !match?.ai_analysis || Object.keys(match?.ai_analysis || {}).length === 0;

	const [reportOpen, setReportOpen] = useState(false);

	useEffect(() => {
		const fetchMatch = async () => {
			if (!id) return;

			try {
				const { data, error } = await supabase
					.from('matches')
					.select(`
						*,
						candidates (
							id,
							name,
							email,
							function,
							skills,
							ai_synthesis
						),
						jobs (
							id,
							title,
							description,
							companies (
								id,
								name
							)
						)
					`)
					.eq('id', id)
					.single();

				if (error) throw error;

				setMatch(data);
			} catch (error) {
				console.error("Error fetching match:", error);
				toast({
					title: "Error",
					description: "Failed to load match details",
					variant: "destructive",
				});
			} finally {
				setLoading(false);
			}
		};

		fetchMatch();
	}, [id]);

	const handleReject = async () => {
		if (!id) return;

		try {
			const { error } = await supabase
				.from('matches')
				.update({ status: 'refused' })
				.eq('id', id);

			if (error) throw error;

			// Update local state
			setMatch({
				...match,
				status: 'refused'
			});

			toast({
				title: "Success",
				description: "Match has been rejected",
				variant: "default",
			});

			// Optionally navigate back to matches list
			// navigate('/matches');
		} catch (error) {
			console.error("Error rejecting match:", error);
			toast({
				title: "Error",
				description: "Failed to reject match",
				variant: "destructive",
			});
		}
	};
	
	const updateMatchStage = async (stageId: string) => {
		if (!id) return;
		
		try {
			const { error } = await supabase
				.from('matches')
				.update({ status: stageId })
				.eq('id', id);
				
			if (error) throw error;
			
			// Update local state
			setMatch({
				...match,
				status: stageId
			});
			
			toast({
				title: "Success",
				description: `Match has been moved to ${stageId} stage`,
				variant: "default",
			});
		} catch (error) {
			console.error("Error updating match stage:", error);
			toast({
				title: "Error",
				description: "Failed to update match stage",
				variant: "destructive",
			});
		}
	};

	// Add a helper for formatting criteria names
	function formatCriteriaName(name: string) {
		return name
			.replace(/_/g, ' ')
			.replace(/\b\w/g, c => c.toUpperCase());
	}

	// Add a simple neutral gauge bar component
	const NeutralGauge = ({ value }: { value: number }) => (
		<div className="flex items-center gap-2 w-full">
			<div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
				<div
					className="h-2 bg-gray-400 rounded-full transition-all"
					style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }}
				/>
			</div>
			<span className="ml-2 text-xs tabular-nums text-gray-700" style={{ minWidth: 32 }}>{value}</span>
		</div>
	);

	// Helper component for pretty analysis rendering
	const DetailedAnalysis = ({ analysis }: { analysis: any }) => {
		if (!analysis || typeof analysis !== "object") return null;
		return (
			<div className="space-y-6">
				{Object.entries(analysis).map(([section, value]: [string, any]) => {
					const pairScores = value["Pair score"] && typeof value["Pair score"] === 'object'
						? Object.entries(value["Pair score"]) as [string, any][]
						: [];
					return (
						<div key={section} className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
							<h5 className="text-lg font-semibold mb-2 text-digger-blue">{section}</h5>
							{value.Explanation && (
								<div className="mb-2 text-gray-700">
									<span className="font-medium">Explanation:</span> {value.Explanation}
								</div>
							)}
							{pairScores.length > 0 && (
								<div className="mb-2">
									<span className="font-medium text-gray-600">Pair Scores:</span>
									<div className="bg-white rounded-md border border-gray-100 mt-2">
										<div className="grid grid-cols-1 gap-y-2 p-2">
											{pairScores.map(([k, v]) => (
												<div key={k} className="flex items-center gap-4">
													<div className="font-mono text-xs text-gray-500 truncate flex-1" style={{fontFamily: 'inherit', fontWeight: 500}}>{formatCriteriaName(k)}</div>
													<div className="w-2/3 max-w-xs min-w-[80px]"><NeutralGauge value={v} /></div>
												</div>
											))}
										</div>
									</div>
								</div>
							)}
							{value["Match Ratio"] !== undefined && (
								<div className="mb-1 text-sm text-gray-600">
									<span className="font-medium">Match Ratio:</span> {value["Match Ratio"]}
								</div>
							)}
							{value["Weighted Score"] !== undefined && (
								<div className="mb-1 text-sm text-gray-600">
									<span className="font-medium">Weighted Score:</span> {value["Weighted Score"]}
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	// PDF Download Handler
	const handleDownloadReport = async () => {
		const reportElement = document.getElementById("match-report-modal-content");
		if (!reportElement) return;

		const canvas = await html2canvas(reportElement, { scale: 2, backgroundColor: '#fff' });
		const imgWidth = 515; // A4 width minus margins (in pt)
		const pageHeight = 742; // A4 height minus margins (in pt)
		const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

		let renderedHeight = 0;
		let pageNum = 0;

		while (renderedHeight < canvas.height) {
			// Create a canvas for the current page
			const pageCanvas = document.createElement("canvas");
			pageCanvas.width = canvas.width;
			pageCanvas.height = Math.min(canvas.height - renderedHeight, (pageHeight * canvas.width) / imgWidth);

			const ctx = pageCanvas.getContext("2d");
			if (ctx) {
				ctx.drawImage(
					canvas,
					0, renderedHeight, canvas.width, pageCanvas.height,
					0, 0, canvas.width, pageCanvas.height
				);
			}

			const pageImgData = pageCanvas.toDataURL("image/png");
			if (pageNum > 0) pdf.addPage();
			pdf.addImage(pageImgData, "PNG", 40, 40, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

			renderedHeight += pageCanvas.height;
			pageNum++;
		}

		pdf.save(`Digger_Match_Report_${match.candidates?.name || "candidate"}_${match.jobs?.title || "job"}.pdf`);
	};

	// Send by Email Handler (fallback: mailto with instructions)
	const handleSendByEmail = async () => {
		const reportElement = document.getElementById("match-report-modal-content");
		if (!reportElement) return;

		const canvas = await html2canvas(reportElement, { scale: 2, backgroundColor: '#fff' });
		const imgWidth = 515;
		const pageHeight = 742;
		const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

		let renderedHeight = 0;
		let pageNum = 0;

		while (renderedHeight < canvas.height) {
			const pageCanvas = document.createElement("canvas");
			pageCanvas.width = canvas.width;
			pageCanvas.height = Math.min(canvas.height - renderedHeight, (pageHeight * canvas.width) / imgWidth);

			const ctx = pageCanvas.getContext("2d");
			if (ctx) {
				ctx.drawImage(
					canvas,
					0, renderedHeight, canvas.width, pageCanvas.height,
					0, 0, canvas.width, pageCanvas.height
				);
			}

			const pageImgData = pageCanvas.toDataURL("image/png");
			if (pageNum > 0) pdf.addPage();
			pdf.addImage(pageImgData, "PNG", 40, 40, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

			renderedHeight += pageCanvas.height;
			pageNum++;
		}

		const pdfBlob = pdf.output("blob");
		const pdfUrl = URL.createObjectURL(pdfBlob);
		window.open(`mailto:?subject=Match Report&body=Please find the match report attached.\n\nIf the PDF is not attached, download it here: ${pdfUrl}`);
		setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
	};

	if (loading) {
		return (
			<div className="animate-pulse p-6">
				<div className="h-12 bg-gray-200 rounded mb-6"></div>
				<div className="space-y-4">
					<div className="h-32 bg-gray-200 rounded"></div>
					<div className="h-64 bg-gray-200 rounded"></div>
				</div>
			</div>
		);
	}

	if (!match) {
		return (
			<div className="p-6">
				<div className="bg-white rounded-lg shadow p-6 text-center">
					<h2 className="text-xl font-semibold mb-2">Match Not Found</h2>
					<p className="text-gray-600">The match you are looking for does not exist or has been deleted.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Back to matches link */}
			<div className="mb-2">
				<Link to="/matches" className="flex items-center text-digger-navy hover:underline text-base font-medium">
					<ArrowLeft className="h-5 w-5 mr-1" />
					Back to matches
				</Link>
			</div>
			<PageHeader 
				title="Match Analysis"
				actions={
					<div className="flex gap-2">
						<Button variant="outline" className="flex items-center gap-2" onClick={() => setReportOpen(true)}>
							<Download className="h-4 w-4" />
							<span>Full Report</span>
						</Button>
					</div>
				}
				className="mb-2"
			/>
			{/* Full Report Modal */}
			<Dialog open={reportOpen} onOpenChange={setReportOpen}>
				<DialogContent className="max-w-4xl w-full h-[90vh] max-h-[90vh] overflow-y-auto p-0">
					<div className="flex flex-col h-full">
						<div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
							<div className="font-bold text-lg">Full Match Report</div>
							<div className="flex gap-2">
								<Button variant="outline" onClick={handleDownloadReport}>Save PDF</Button>
								<Button variant="outline" onClick={handleSendByEmail}>Send by Email</Button>
							</div>
						</div>
						<div id="match-report-modal-content" className="overflow-y-auto p-8 bg-white" style={{ minHeight: '70vh' }}>
							{/* Digger logo (styled div and text) */}
							<div style={{ textAlign: 'center', marginBottom: 8 }}>
								<div style={{
									width: 48, height: 48, borderRadius: 12,
									background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
									color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
									fontWeight: 700, fontSize: 32, margin: "0 auto 8px auto", boxShadow: "0 2px 8px #e0e7ef"
								}}>
									D
								</div>
								<span style={{
									fontWeight: 700, fontSize: 22, color: "#2563eb", letterSpacing: 1, display: "block", textAlign: "center", marginBottom: 24
								}}>
									Digger
								</span>
							</div>
							<h1 style={{ fontSize: 28, fontWeight: 700, color: '#0E2356', marginBottom: 8 }}>Match Report</h1>
							<div style={{ marginBottom: 16, color: '#233772', fontSize: 18 }}>
								{match.candidates?.name} &mdash; {match.jobs?.title} ({match.jobs?.companies?.name})
							</div>
							<div style={{ marginBottom: 16 }}>
								<strong>Score:</strong> {match.score}%
							</div>
							<div style={{ display: 'flex', gap: 32, marginBottom: 24 }}>
								<div style={{ flex: 1 }}>
									<h3 style={{ color: '#16a34a', fontWeight: 600, fontSize: 18 }}>Key Strengths</h3>
									<ul>
										{match.strengths?.map((s: string, i: number) => <li key={i}>{s}</li>)}
									</ul>
								</div>
								<div style={{ flex: 1 }}>
									<h3 style={{ color: '#dc2626', fontWeight: 600, fontSize: 18 }}>Areas for Improvement</h3>
									<ul>
										{match.weaknesses?.map((w: string, i: number) => <li key={i}>{w}</li>)}
									</ul>
								</div>
							</div>
							<div style={{ marginBottom: 24 }}>
								<h3 style={{ color: '#0E2356', fontWeight: 600, fontSize: 18 }}>AI Analysis</h3>
								<div style={{ marginBottom: 12 }}>
									<strong>Overall Recommendation:</strong> {match.ai_analysis?.analysis?.final_recommendation}
								</div>
								{/* Render all detailed analysis sections */}
								{match.ai_analysis?.analysis?.matching_analysis && Object.entries(match.ai_analysis.analysis.matching_analysis).map(([section, value]: [string, any]) => (
									<div key={section} style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
										<h4 style={{ color: '#0E2356', fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{section}</h4>
										{value.Explanation && <div style={{ marginBottom: 4 }}><strong>Explanation:</strong> {value.Explanation}</div>}
										{value["Pair score"] && (
											<div style={{ marginBottom: 4 }}>
												<strong>Pair Scores:</strong>
												<ul style={{ margin: 0, paddingLeft: 16 }}>
													{(Object.entries(value["Pair score"]) as [string, any][]).map(([k, v]) => (
														<li key={k}><span style={{ fontFamily: 'monospace', color: '#64748b' }}>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>: {v}</li>
													))}
												</ul>
											</div>
										)}
										{value["Match Ratio"] !== undefined && (
											<div><strong>Match Ratio:</strong> {value["Match Ratio"]}</div>
										)}
										{value["Weighted Score"] !== undefined && (
											<div><strong>Weighted Score:</strong> {value["Weighted Score"]}</div>
										)}
									</div>
								))}
							</div>
							<div style={{ marginTop: 32, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
								Report generated by Digger
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			{/* Score and preview cards */}
			<div className="bg-white rounded-lg shadow p-6 flex flex-col items-center w-full mb-4">
				<div className="flex flex-col items-center w-full">
					<div className="h-20 w-20 bg-blue-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mb-2">
						{typeof match.score === 'number' ? `${match.score}%` : ''}
					</div>
					<div className="text-sm text-gray-500 mb-1">Match Score</div>
					{match.status !== 'refused' && (
						<Button variant="outline" className="flex items-center gap-2 mt-2" onClick={handleReject}>
							<X className="h-4 w-4" />
							<span>Reject</span>
						</Button>
					)}
				</div>
				{/* Candidate & Job cards side by side */}
				<div className="flex flex-col md:flex-row gap-4 w-full mt-6">
					{/* Candidate Preview Card */}
					<div className="bg-gray-50 rounded-lg shadow-sm p-4 flex items-center gap-3 flex-1 min-w-0">
						<div className="h-12 w-12 bg-digger-light-gray rounded-full flex items-center justify-center">
							<User className="h-6 w-6 text-gray-600" />
						</div>
						<div className="flex-1 min-w-0">
							<div className="font-semibold truncate">{match.candidates?.name}</div>
							<div className="text-xs text-gray-500 truncate">{match.candidates?.function}</div>
							<div className="text-xs text-gray-400 truncate">{match.candidates?.email}</div>
						</div>
						<Link to={`/candidates/${match.candidates?.id}`} className="text-xs text-digger-blue hover:underline whitespace-nowrap">View</Link>
					</div>
					{/* Job Preview Card */}
					<div className="bg-gray-50 rounded-lg shadow-sm p-4 flex items-center gap-3 flex-1 min-w-0">
						<div className="h-12 w-12 bg-digger-light-gray rounded-full flex items-center justify-center">
							<Briefcase className="h-6 w-6 text-gray-600" />
						</div>
						<div className="flex-1 min-w-0">
							<div className="font-semibold truncate">{match.jobs?.title}</div>
							<div className="text-xs text-gray-500 truncate">{match.jobs?.companies?.name}</div>
						</div>
						<Link to={`/jobs/${match.jobs?.id}`} className="text-xs text-digger-blue hover:underline whitespace-nowrap">View</Link>
					</div>
				</div>
			</div>
			{/* Clickable Timeline */}
			<div className="bg-white rounded-lg shadow p-6 mb-4">
				<h3 className="text-sm font-medium mb-4">Recruitment Progress</h3>
				<div className="relative">
					<div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200"></div>
					<div className="flex justify-between relative">
						{KANBAN_STATUSES.map((status, index) => {
							const isActive = match.status === status.id;
							const isRefused = match.status === 'refused';
							const isPast = !isRefused 
								? KANBAN_STATUSES.findIndex(s => s.id === match.status) >= index
								: false;
							const isClickable = isRefused 
								? status.id !== 'refused'
								: (index <= KANBAN_STATUSES.findIndex(s => s.id === match.status) + 1);
							return (
								<div 
									key={status.id} 
									className={`flex flex-col items-center relative z-10 ${isClickable ? 'cursor-pointer' : ''}`}
									onClick={() => isClickable ? updateMatchStage(status.id) : null}
									title={isClickable ? `Set status to ${status.name}` : `Cannot skip to this status`}
								>
									{isActive && status.id === 'refused' ? (
										<div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center mb-2 border-2 border-red-200">
											<X className="w-4 h-4" />
										</div>
									) : isActive ? (
										<div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mb-2 border-2 border-blue-200">
											<Check className="w-4 h-4" />
										</div>
									) : isPast ? (
										<Badge variant="success" className="w-8 h-8 p-0 flex items-center justify-center rounded-full mb-2">
											<Check className="w-4 h-4" />
										</Badge>
									) : (
										<Badge variant="outline" className="w-8 h-8 p-0 flex items-center justify-center rounded-full mb-2 bg-white">
											<span className="w-3 h-3 rounded-full bg-gray-200"></span>
										</Badge>
									)}
									<Badge 
										variant={
											isActive && status.id === 'refused' ? "destructive" : 
											isActive ? "info" : 
											isPast ? "success" : 
											"outline"
										} 
										className={`text-xs px-2 py-0.5 ${isActive ? 'font-medium' : ''}`}
									>
										{status.name}
									</Badge>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			{/* Tabs Navigation */}
			<div className="w-full">
				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList className="w-full mb-4 flex flex-wrap gap-x-2 gap-y-2 bg-gray-100 rounded-lg p-2">
						<TabsTrigger value="overview" className="flex-shrink-0 flex items-center gap-2 min-w-max">
							<FileText className="h-4 w-4" />
							<span>Overview</span>
						</TabsTrigger>
						<TabsTrigger value="comments" className="flex-shrink-0 flex items-center gap-2 min-w-max">
							<MessageSquare className="h-4 w-4" />
							<span>Comments</span>
						</TabsTrigger>
						<TabsTrigger value="interview-prep" className="flex-shrink-0 flex items-center gap-2 min-w-max">
							<Brain className="h-4 w-4" />
							<span>Interview Prep</span>
						</TabsTrigger>
						<TabsTrigger value="tasks" className="flex-shrink-0 flex items-center gap-2 min-w-max">
							<ClipboardCheck className="h-4 w-4" />
							<span>Tasks</span>
						</TabsTrigger>
					</TabsList>
					
					{/* Overview Tab Content */}
					<TabsContent value="overview" className="md:w-full">
						<div className="bg-white rounded-lg shadow p-6">
							{/* Strengths and Weaknesses side by side at the top */}
							<div className="flex flex-col md:flex-row gap-6 mb-6">
								{/* Key Strengths Card */}
								<div className="flex-1 bg-green-50/60 backdrop-blur-md rounded-xl p-6 shadow-sm border border-green-100" style={{background: 'linear-gradient(135deg,rgba(34,197,94,0.08),rgba(34,197,94,0.03))', boxShadow: '0 4px 24px 0 rgba(34,197,94,0.08)'}}>
									<h4 className="font-medium text-green-600 mb-2">Key Strengths</h4>
									<ul className="list-disc pl-5 space-y-2 text-base">
										{match.strengths?.map((strength: string, index: number) => (
											<li key={index} className="text-gray-700">{strength}</li>
										))}
									</ul>
								</div>
								{/* Areas for Improvement Card */}
								<div className="flex-1 bg-red-50/60 backdrop-blur-md rounded-xl p-6 shadow-sm border border-red-100" style={{background: 'linear-gradient(135deg,rgba(239,68,68,0.08),rgba(239,68,68,0.03))', boxShadow: '0 4px 24px 0 rgba(239,68,68,0.08)'}}>
									<h4 className="font-medium text-red-600 mb-2">Areas for Improvement</h4>
									<ul className="list-disc pl-5 space-y-2 text-base">
										{match.weaknesses?.map((weakness: string, index: number) => (
											<li key={index} className="text-gray-700">{weakness}</li>
										))}
									</ul>
								</div>
							</div>
							{/* Rest of the analysis accordion */}
							<Accordion 
								type="multiple" 
								value={openAccordionItems} 
								onValueChange={setOpenAccordionItems}
							>
								<AccordionItem value="analysis">
									<AccordionTrigger>AI Analysis</AccordionTrigger>
									<AccordionContent>
										<div className="prose max-w-none">
											{match.ai_analysis && (
												<div className="space-y-4">
													<div>
														<h4 className="font-medium">Overall Analysis</h4>
														<p className="text-gray-700">{match.ai_analysis.analysis.final_recommendation}</p>
													</div>
													{match.ai_analysis.analysis.matching_analysis && (
														<DetailedAnalysis analysis={match.ai_analysis.analysis.matching_analysis} />
													)}
												</div>
											)}
										</div>
									</AccordionContent>
								</AccordionItem>
								<AccordionItem value="skills">
									<AccordionTrigger>Skills Analysis</AccordionTrigger>
									<AccordionContent>
										<div className="space-y-4">
											<div>
												<h4 className="font-medium text-sm text-gray-500 mb-2">Candidate Skills</h4>
												<div className="flex flex-wrap gap-2">
													{match.candidates?.skills?.map((skill: string, index: number) => (
														<SkillBadge key={index} label={skill} variant="primary" />
													))}
												</div>
											</div>
										</div>
									</AccordionContent>
								</AccordionItem>
							</Accordion>
						</div>
					</TabsContent>
					
					{/* Comments Tab Content */}
					<TabsContent value="comments" className="md:w-full">
						<div className="bg-white rounded-lg shadow p-6">
							<MatchComments matchId={id} />
						</div>
					</TabsContent>
					
					{/* Interview Prep Tab Content */}
					<TabsContent value="interview-prep" className="md:w-full">
						<div className="bg-white rounded-lg shadow p-6">
							<InterviewPrep matchId={id} />
						</div>
					</TabsContent>
					
					{/* Task Tab Content */}
					<TabsContent value="task" className="md:w-full">
						<div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
							Task feature coming soon.
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

export default MatchDetail;
